/**
 * 모던 디자인 시스템 토큰
 * 20년차 웹앱 디자이너 관점에서 구축한 일관된 디자인 시스템
 */

// ===== 색상 팔레트 =====
export const colors = {
    // Primary Colors (브랜드 컬러)
    primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6', // 메인 브랜드 컬러
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
        950: '#172554',
    },

    // Secondary Colors (보조 컬러)
    secondary: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
        950: '#020617',
    },

    // Success Colors
    success: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
    },

    // Warning Colors
    warning: {
        50: '#fffbeb',
        100: '#fef3c7',
        200: '#fde68a',
        300: '#fcd34d',
        400: '#fbbf24',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
        800: '#92400e',
        900: '#78350f',
    },

    // Error Colors
    error: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d',
    },

    // Neutral Colors
    neutral: {
        0: '#ffffff',
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#e5e5e5',
        300: '#d4d4d4',
        400: '#a3a3a3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        950: '#0a0a0a',
    },

    // Semantic Colors
    semantic: {
        background: {
            primary: '#ffffff',
            secondary: '#f8fafc',
            tertiary: '#f1f5f9',
            elevated: '#ffffff',
        },
        text: {
            primary: '#0f172a',
            secondary: '#475569',
            tertiary: '#64748b',
            disabled: '#94a3b8',
            inverse: '#ffffff',
        },
        border: {
            primary: '#e2e8f0',
            secondary: '#cbd5e1',
            focus: '#3b82f6',
            error: '#ef4444',
        },
    },
} as const;

// ===== 타이포그래피 =====
export const typography = {
    fontFamily: {
        sans: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Oxygen',
            'Ubuntu',
            'Cantarell',
            'Fira Sans',
            'Droid Sans',
            'Helvetica Neue',
            'sans-serif',
        ].join(', '),
        mono: [
            'JetBrains Mono',
            'Fira Code',
            'Monaco',
            'Consolas',
            'Liberation Mono',
            'Courier New',
            'monospace',
        ].join(', '),
    },

    fontSize: {
        xs: '0.75rem',    // 12px
        sm: '0.875rem',   // 14px
        base: '1rem',     // 16px
        lg: '1.125rem',   // 18px
        xl: '1.25rem',    // 20px
        '2xl': '1.5rem',  // 24px
        '3xl': '1.875rem', // 30px
        '4xl': '2.25rem', // 36px
        '5xl': '3rem',    // 48px
        '6xl': '3.75rem', // 60px
    },

    fontWeight: {
        thin: '100',
        extralight: '200',
        light: '300',
        normal: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
        extrabold: '800',
        black: '900',
    },

    lineHeight: {
        none: '1',
        tight: '1.25',
        snug: '1.375',
        normal: '1.5',
        relaxed: '1.625',
        loose: '2',
    },

    letterSpacing: {
        tighter: '-0.05em',
        tight: '-0.025em',
        normal: '0em',
        wide: '0.025em',
        wider: '0.05em',
        widest: '0.1em',
    },
} as const;

// ===== 간격 시스템 =====
export const spacing = {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
    32: '8rem',     // 128px
    40: '10rem',    // 160px
    48: '12rem',    // 192px
    56: '14rem',    // 224px
    64: '16rem',    // 256px
} as const;

// ===== 보더 반경 =====
export const borderRadius = {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    '3xl': '1.5rem',  // 24px
    full: '9999px',
} as const;

// ===== 그림자 =====
export const shadows = {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

    // 컬러 그림자
    primary: '0 4px 14px 0 rgba(59, 130, 246, 0.15)',
    success: '0 4px 14px 0 rgba(34, 197, 94, 0.15)',
    warning: '0 4px 14px 0 rgba(245, 158, 11, 0.15)',
    error: '0 4px 14px 0 rgba(239, 68, 68, 0.15)',
} as const;

// ===== 애니메이션 =====
export const animations = {
    duration: {
        fast: '150ms',
        normal: '200ms',
        slow: '300ms',
        slower: '500ms',
    },

    easing: {
        linear: 'linear',
        ease: 'ease',
        easeIn: 'ease-in',
        easeOut: 'ease-out',
        easeInOut: 'ease-in-out',
        bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
        smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },

    keyframes: {
        fadeIn: `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `,
        slideUp: `
      @keyframes slideUp {
        from { 
          opacity: 0;
          transform: translateY(20px);
        }
        to { 
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
        slideDown: `
      @keyframes slideDown {
        from { 
          opacity: 0;
          transform: translateY(-20px);
        }
        to { 
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
        scaleIn: `
      @keyframes scaleIn {
        from { 
          opacity: 0;
          transform: scale(0.95);
        }
        to { 
          opacity: 1;
          transform: scale(1);
        }
      }
    `,
        spin: `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `,
        pulse: `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `,
    },
} as const;

// ===== 브레이크포인트 =====
export const breakpoints = {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
} as const;

// ===== Z-Index =====
/** 앱 전역 스택(모달·사이드패널·레이아웃) — 숫자가 클수록 위 */
export const zIndex = {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    /** 모바일 하단 탭 */
    layoutBottomNav: 100,
    /** MainLayout 좁은 화면 고정 헤더 */
    layoutMobileHeader: 999,
    /** 오버레이 사이드바·레거시 툴팁 등 동일 레벨 배경 */
    overlayBackdrop: 1000,
    /** 오버레이 패널·햄버거 버튼 */
    overlayPanel: 1001,
    /** 데스크톱 고정 사이드바 */
    desktopSidebar: 1000,
    dropdown: 1000,
    /** 캘린더/날짜 팝업 — 헤더·사이드바 위, 모달 아래 */
    datePickerPopup: 1100,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
} as const;

// ===== 그라데이션 =====
export const gradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    warning: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    error: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    neutral: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',

    // 배경 그라데이션
    background: {
        primary: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        secondary: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        card: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
    },
} as const;

// ===== 컴포넌트별 토큰 =====
export const components = {
    button: {
        height: {
            sm: '2rem',      // 32px
            md: '2.5rem',    // 40px
            lg: '3rem',      // 48px
            xl: '3.5rem',    // 56px
        },
        padding: {
            sm: '0.5rem 1rem',
            md: '0.75rem 1.5rem',
            lg: '1rem 2rem',
            xl: '1.25rem 2.5rem',
        },
    },

    input: {
        height: {
            sm: '2rem',
            md: '2.5rem',
            lg: '3rem',
        },
        padding: {
            sm: '0.5rem 0.75rem',
            md: '0.75rem 1rem',
            lg: '1rem 1.25rem',
        },
    },

    card: {
        padding: {
            sm: '1rem',
            md: '1.5rem',
            lg: '2rem',
            xl: '2.5rem',
        },
    },

    sidebar: {
        width: {
            sm: '16rem',     // 256px
            md: '18rem',     // 288px
            lg: '20rem',     // 320px
        },
    },
} as const;

// ===== 유틸리티 함수 =====
export const utils = {
    // 색상 투명도 조절
    withOpacity: (color: string, opacity: number) =>
        `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,

    // 반응형 미디어 쿼리
    media: {
        xs: `@media (min-width: ${breakpoints.xs})`,
        sm: `@media (min-width: ${breakpoints.sm})`,
        md: `@media (min-width: ${breakpoints.md})`,
        lg: `@media (min-width: ${breakpoints.lg})`,
        xl: `@media (min-width: ${breakpoints.xl})`,
        '2xl': `@media (min-width: ${breakpoints['2xl']})`,
    },

    // 다크모드 미디어 쿼리
    dark: '@media (prefers-color-scheme: dark)',

    // 모션 감소 미디어 쿼리
    reducedMotion: '@media (prefers-reduced-motion: reduce)',
} as const;

// ===== 타입 정의 =====
export type ColorScale = keyof typeof colors.primary;
export type SpacingScale = keyof typeof spacing;
export type FontSizeScale = keyof typeof typography.fontSize;
export type FontWeightScale = keyof typeof typography.fontWeight;
export type BorderRadiusScale = keyof typeof borderRadius;
export type ShadowScale = keyof typeof shadows;
export type BreakpointScale = keyof typeof breakpoints;
export type ZIndexScale = keyof typeof zIndex;
